/**
 * Phase 2 §4 — Pay-run anomaly detection.
 *
 * Pure rule engine. Takes a fully-prepared input (timesheets +
 * classifications + screenings) and returns a list of detected anomalies.
 * No DB access; the server-side runner in
 * `apps/web/src/lib/payroll/anomaly-runner.ts` loads the data and
 * persists results to `payroll_anomalies`.
 *
 * Rule severity:
 *   BLOCK — must be resolved before pay-run can advance to APPROVED.
 *   WARN  — manager review required; doesn't block advance.
 *   INFO  — informational; surfaces in queue without blocking.
 *
 * Initial 10 rules (Phase 2 §4.3 spec). All are tunable per-tenant via
 * a future config table; for v1 thresholds are constants.
 */

import { SCHADS_MINIMUM_SHIFT_HOURS } from './regulatory-rates';
import { resolveSingleEffectiveDatedRow } from './payroll/effective-dated-history';

export type AnomalySeverity = 'INFO' | 'WARN' | 'BLOCK';
export type AnomalyScope = 'TIMESHEET' | 'WORKER_WEEK' | 'PAY_RUN';

export interface Anomaly {
  ruleCode: string;
  severity: AnomalySeverity;
  scope: AnomalyScope;
  scopeRefType: string; // e.g. 'timesheet', 'staff_week', 'pay_run'
  scopeRef: string; // FK
  message: string;
  suggestedAction?: string;
}

export interface AnomalyTimesheet {
  id: string;
  staffId: string;
  paymentMethod?: string | null;
  isSalaryFlat?: boolean | null;
  date: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  actualStart: string;
  actualEnd: string | null; // null indicates NO_CLOCKOUT
  breakMinutes: number;
  totalHours: number;
  status: string;
  rosterShiftId: string | null;
  hasSleepover: boolean;
  brokenShiftCount: number; // 0/1/2
  hasUniformAllowance: boolean;
  hasLaundryAllowance: boolean;
  hasFirstAidResponsibility: boolean;
}

export interface AnomalyClassification {
  staffId: string;
  awardCode: string | null; // null/UNCLASSIFIED → MISSING_CLASSIFICATION
  employmentType: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface AnomalyScreening {
  staffId: string;
  screeningType: string; // POLICE, NDIS, AHPRA, FIRST_AID, etc.
  expiresAt: string | null;
  status: string;
}

export interface DetectAnomaliesInput {
  timesheets: AnomalyTimesheet[];
  classifications: AnomalyClassification[]; // covering shift dates
  screenings: AnomalyScreening[]; // by staff_id
  /** Per-shift allowance amounts already computed (from breakdown) — used by WEEKLY_CAP_BREACH. */
  perShiftAllowances?: Array<{
    timesheetId: string;
    staffId: string;
    weekStart: string; // ISO Monday
    firstAidAmount: number;
    uniformAmount: number;
    laundryAmount: number;
  }>;
  /** Tenant-supplied weekly caps (from override or constants). */
  weeklyCaps?: { firstAid: number; uniform: number; laundry: number };
}

/** Diff hours between two ISO date-times. Returns 0 if missing. */
function hoursBetween(start: string, end: string | null): number {
  if (!end) return 0;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(0, ms / (1000 * 60 * 60));
}

/**
 * ISO Monday (UTC) for a given date string. The input is a plain
 * YYYY-MM-DD calendar date with no timezone; UTC arithmetic is correct
 * here because we're week-bucketing date strings, not wall-clock times.
 */
function isoMonday(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d.getTime() + diff * 24 * 60 * 60 * 1000);
  // eslint-disable-next-line statura/no-utc-date-string -- intentional UTC date arithmetic; input is a calendar date string
  return monday.toISOString().slice(0, 10);
}

function isSalaryFlatTimesheet(timesheet: AnomalyTimesheet): boolean {
  if (timesheet.isSalaryFlat === true) return true;
  return (
    String(timesheet.paymentMethod ?? '')
      .trim()
      .toUpperCase() === 'SALARY_FLAT'
  );
}

export function detectAnomaliesForPayRun(input: DetectAnomaliesInput): Anomaly[] {
  const out: Anomaly[] = [];

  // Index helpers
  const classByStaff = new Map<string, AnomalyClassification[]>();
  for (const c of input.classifications) {
    if (!classByStaff.has(c.staffId)) classByStaff.set(c.staffId, []);
    classByStaff.get(c.staffId)!.push(c);
  }
  const screeningsByStaff = new Map<string, AnomalyScreening[]>();
  for (const s of input.screenings) {
    if (!screeningsByStaff.has(s.staffId)) screeningsByStaff.set(s.staffId, []);
    screeningsByStaff.get(s.staffId)!.push(s);
  }

  // Per-timesheet rules
  for (const t of input.timesheets) {
    // NO_CLOCKOUT
    if (!t.actualEnd) {
      out.push({
        ruleCode: 'NO_CLOCKOUT',
        severity: 'BLOCK',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Timesheet on ${t.date} has no clock-out recorded.`,
        suggestedAction: 'Manager must record actual_end before pay run can advance.',
      });
      continue; // can't run other duration-based rules
    }

    const durationHours = hoursBetween(t.actualStart, t.actualEnd);
    const paidHours = Math.max(0, durationHours - t.breakMinutes / 60);

    // MISSING_BREAK_LONG_SHIFT
    if (paidHours > 5 && t.breakMinutes === 0) {
      out.push({
        ruleCode: 'MISSING_BREAK_LONG_SHIFT',
        severity: 'WARN',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Shift on ${t.date} is ${paidHours.toFixed(2)}h with no break recorded.`,
        suggestedAction:
          'Verify the worker took a break. SCHADS clause 27 requires a 30-min break after 5 hours.',
      });
    }

    // BROKEN_SHIFT_NOT_CLAIMED — a recorded break of >= 2 hours inside a
    // single timesheet is the signal of a broken-shift pattern. Normal meal
    // breaks are 30–60 min; a 2h+ break almost always means the worker
    // started, paused for personal time, and resumed — i.e. a broken shift
    // that should claim SCHADS cl 20.12. This rule fires when that pattern
    // is detected but `brokenShiftCount` was not flagged.
    if (t.breakMinutes >= 120 && t.brokenShiftCount === 0) {
      const breakHours = t.breakMinutes / 60;
      out.push({
        ruleCode: 'BROKEN_SHIFT_NOT_CLAIMED',
        severity: 'WARN',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Shift on ${t.date} recorded a ${breakHours.toFixed(2)}h break but no broken-shift allowance flagged.`,
        suggestedAction:
          'Confirm broken-shift allowance (SCHADS cl 20.12) applies and flag on the timesheet.',
      });
    }

    // HOURS_OVER_ROSTER
    if (t.scheduledStart && t.scheduledEnd) {
      const scheduledHours = hoursBetween(t.scheduledStart, t.scheduledEnd);
      if (paidHours > scheduledHours + 0.5) {
        out.push({
          ruleCode: 'HOURS_OVER_ROSTER',
          severity: 'WARN',
          scope: 'TIMESHEET',
          scopeRefType: 'timesheet',
          scopeRef: t.id,
          message: `Worked ${paidHours.toFixed(2)}h vs scheduled ${scheduledHours.toFixed(2)}h.`,
          suggestedAction: 'Validate the overrun was authorised.',
        });
      }
    }

    // UNSCHEDULED_SHIFT
    if (!t.rosterShiftId) {
      out.push({
        ruleCode: 'UNSCHEDULED_SHIFT',
        severity: 'WARN',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Timesheet on ${t.date} has no linked roster shift.`,
        suggestedAction: 'Validate worker was authorised for this shift.',
      });
    }

    // MIN_ENGAGEMENT_BREACH
    const cls = resolveSingleEffectiveDatedRow(classByStaff.get(t.staffId) ?? [], t.date);
    const employmentType = cls?.employmentType ?? 'UNKNOWN';
    const minHours =
      employmentType === 'CASUAL'
        ? SCHADS_MINIMUM_SHIFT_HOURS.casual
        : employmentType === 'PART_TIME'
          ? SCHADS_MINIMUM_SHIFT_HOURS.partTime
          : SCHADS_MINIMUM_SHIFT_HOURS.fullTime;
    if (paidHours < minHours) {
      out.push({
        ruleCode: 'MIN_ENGAGEMENT_BREACH',
        severity: 'WARN',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Shift on ${t.date} is ${paidHours.toFixed(2)}h, below the ${minHours}h minimum for ${employmentType}.`,
        suggestedAction:
          'Check minimum-engagement clause (SCHADS cl 10.4 / 25.5) — may require pay top-up.',
      });
    }

    // MISSING_CLASSIFICATION_AT_SHIFT
    if (!isSalaryFlatTimesheet(t) && (!cls || !cls.awardCode || cls.awardCode === 'UNCLASSIFIED')) {
      out.push({
        ruleCode: 'MISSING_CLASSIFICATION_AT_SHIFT',
        severity: 'BLOCK',
        scope: 'TIMESHEET',
        scopeRefType: 'timesheet',
        scopeRef: t.id,
        message: `Worker has no valid classification covering ${t.date} (current: ${cls?.awardCode ?? 'no row'}).`,
        suggestedAction:
          'HR review required: insert a CORRECTION row in staff_classification_history with a SCHADS-mappable classification before snapshot.',
      });
    }

    // EXPIRED_SCREENING_AT_SHIFT
    const screenings = screeningsByStaff.get(t.staffId) ?? [];
    for (const sc of screenings) {
      const isCriticalScreening = ['POLICE', 'NDIS', 'AHPRA', 'FIRST_AID', 'WWCC'].includes(
        sc.screeningType,
      );
      if (!isCriticalScreening) continue;
      if (sc.expiresAt && sc.expiresAt < t.date) {
        out.push({
          ruleCode: 'EXPIRED_SCREENING_AT_SHIFT',
          severity: 'BLOCK',
          scope: 'TIMESHEET',
          scopeRefType: 'timesheet',
          scopeRef: t.id,
          message: `${sc.screeningType} screening expired on ${sc.expiresAt} (shift date ${t.date}).`,
          suggestedAction:
            'Cannot pay this shift until the screening is renewed and the worker re-cleared.',
        });
        break; // one per shift is enough
      }
    }
  }

  // Worker-week rules: WEEKLY_HOURS_OVER_38
  const hoursByWorkerWeek = new Map<string, number>();
  for (const t of input.timesheets) {
    if (!t.actualEnd) continue;
    const weekStart = isoMonday(t.date);
    const key = `${t.staffId}|${weekStart}`;
    const cls = resolveSingleEffectiveDatedRow(classByStaff.get(t.staffId) ?? [], t.date);
    if (cls?.employmentType === 'CASUAL') continue; // INFO rule fires only for permanents
    const paidHours = Math.max(0, hoursBetween(t.actualStart, t.actualEnd) - t.breakMinutes / 60);
    hoursByWorkerWeek.set(key, (hoursByWorkerWeek.get(key) ?? 0) + paidHours);
  }
  for (const [key, hours] of hoursByWorkerWeek) {
    if (hours > 38) {
      out.push({
        ruleCode: 'WEEKLY_HOURS_OVER_38',
        severity: 'INFO',
        scope: 'WORKER_WEEK',
        scopeRefType: 'staff_week',
        scopeRef: key,
        message: `Permanent worker accrued ${hours.toFixed(2)}h in week starting ${key.split('|')[1]} (>38h).`,
        suggestedAction: 'Check if 10-hour-day agreement covers this; otherwise overtime applies.',
      });
    }
  }

  // WEEKLY_CAP_BREACH — fires when summed allowance amounts per week exceed cap
  if (input.perShiftAllowances && input.weeklyCaps) {
    const sumsByWeek = new Map<
      string,
      { firstAid: number; uniform: number; laundry: number; staffId: string; weekStart: string }
    >();
    for (const a of input.perShiftAllowances) {
      const key = `${a.staffId}|${a.weekStart}`;
      const existing = sumsByWeek.get(key) ?? {
        firstAid: 0,
        uniform: 0,
        laundry: 0,
        staffId: a.staffId,
        weekStart: a.weekStart,
      };
      existing.firstAid += a.firstAidAmount;
      existing.uniform += a.uniformAmount;
      existing.laundry += a.laundryAmount;
      sumsByWeek.set(key, existing);
    }
    for (const [key, sums] of sumsByWeek) {
      const breaches: string[] = [];
      if (sums.firstAid > input.weeklyCaps.firstAid)
        breaches.push(
          `FIRST_AID $${sums.firstAid.toFixed(2)} > $${input.weeklyCaps.firstAid.toFixed(2)}`,
        );
      if (sums.uniform > input.weeklyCaps.uniform)
        breaches.push(
          `UNIFORM $${sums.uniform.toFixed(2)} > $${input.weeklyCaps.uniform.toFixed(2)}`,
        );
      if (sums.laundry > input.weeklyCaps.laundry)
        breaches.push(
          `LAUNDRY $${sums.laundry.toFixed(2)} > $${input.weeklyCaps.laundry.toFixed(2)}`,
        );
      if (breaches.length) {
        out.push({
          ruleCode: 'WEEKLY_CAP_BREACH',
          severity: 'WARN',
          scope: 'WORKER_WEEK',
          scopeRefType: 'staff_week',
          scopeRef: key,
          message: `Pre-cap allowance total breaches weekly cap: ${breaches.join('; ')}.`,
          suggestedAction:
            'snapshotPayRun applies caps automatically. This warning surfaces the original (pre-cap) breach for awareness.',
        });
      }
    }
  }

  return out;
}
